import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { catchError, map, Observable, of } from "rxjs";
import { IntegrationStore } from "src/app/features/common-data/stores/integration.store";
import { Integration } from "src/app/shared/models/integration.model";

export interface CustomerIntegrationRouteData {
    vendor: { id: string; name: string };
}

@Injectable({ providedIn: 'root' })
export class CustomerIntegrationGuard implements CanActivate {

    constructor(
        private router: Router,
        private integrationStore: IntegrationStore
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        const integrationIdParam = route.paramMap.get('id');

        if (!integrationIdParam) {
            console.error('Integration ID Parameter ist erforderlich');
            return false;
        }

        const integrationId = +integrationIdParam;

        // Strategie 1: Prüfe ob Daten über Router State mitgegeben wurden
        const navigationState = this.router.getCurrentNavigation()?.extras?.state;
        if (navigationState?.['integrationData']) {
            route.data = {
                ...route.data,
                integrationData: navigationState['integrationData']
            };
            console.log('Client Integration: Daten aus Navigation State geladen');
            return true;
        }

        // Strategie 2: Versuche aus Store zu laden
        let integration = this.integrationStore.getIntegrationById(integrationId);

        if (integration) {
            // Integration im Store vorhanden
            this.setIntegrationData(route, integration);
            console.log('Client Integration: Daten aus Store geladen');
            return true;
        }

        // Strategie 3: Lade vom Server (bei Direktaufruf der URL)
        if (integrationId > 0) {
            console.log('Client Integration: Lade Daten vom Server...');
            return this.integrationStore.loadIntegrationById(integrationId).pipe(
                map((integration) => {
                    this.setIntegrationData(route, integration);
                    console.log('Client Integration: Daten vom Server geladen');
                    return true;
                }),
                catchError((error) => {
                    console.error('Fehler beim Laden der Integration:', error);
                    // Fallback bei Fehler
                    this.setFallbackData(route);
                    return of(true);
                })
            );
        }

        // Strategie 4: Fallback für neue Integration oder ungültige ID
        console.warn('Client Integration: Keine State-Daten vorhanden, verwende Fallback');
        this.setFallbackData(route);
        return true;
    }

    private setIntegrationData(route: ActivatedRouteSnapshot, integration: Integration) {
        const integrationData: CustomerIntegrationRouteData = {
            vendor: {
                id: integration.vendor?.id?.toString() || '',
                name: integration.vendor?.name || 'Unbekannter Lieferant'
            }
        };

        route.data = {
            ...route.data,
            integrationData
        };
    }

    private setFallbackData(route: ActivatedRouteSnapshot) {
        const integrationData: CustomerIntegrationRouteData = {
            vendor: {
                id: '',
                name: 'Lieferant auswählen'
            }
        };

        route.data = {
            ...route.data,
            integrationData
        };
    }
}